import React, { useEffect, useState } from "react";
import axios from "../api/Axios";

const InstitutionCampusDropdown = ({
  institutionValue,
  campusValue,
  onInstitutionChange,
  onCampusChange,
  institutionLabel = "Institution",
  campusLabel = "Campus",
  disabled = false,
  institutionDisabled = false,
  campusDisabled = false,
  validationErrors = {},
}) => {
  const [institutions, setInstitutions] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(institutionValue || "");
  const [selectedCampus, setSelectedCampus] = useState(campusValue || "");
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [loadingCampuses, setLoadingCampuses] = useState(false);

  // Fetch all institutions on mount
  useEffect(() => {
    setLoadingInstitutions(true);
    axios
      .get("/api/institution_view/")
      .then((res) => {
        const sortedInstitutions = [...res.data].sort((a, b) => a.name.localeCompare(b.name));
        setInstitutions(sortedInstitutions);
      })
      .catch(() => setInstitutions([]))
      .finally(() => setLoadingInstitutions(false));
  }, []);

  // Fetch campuses when institution changes
  useEffect(() => {
    if (selectedInstitution) {
      setLoadingCampuses(true);
      axios
        .get(`/api/institutions/${selectedInstitution}/campuses/`)
        .then((res) => {
          setCampuses(res.data);
        })
        .catch(() => setCampuses([]))
        .finally(() => setLoadingCampuses(false));
    } else {
      setCampuses([]);
      setSelectedCampus("");
    }
  }, [selectedInstitution]);

  // Handle institution change
  const handleInstitutionChange = (e) => {
    const value = e.target.value;
    setSelectedInstitution(value);
    setSelectedCampus("");
    if (onInstitutionChange) onInstitutionChange(value);
    if (onCampusChange) onCampusChange(""); // Reset campus
  };

  // Handle campus change
  const handleCampusChange = (e) => {
    const value = e.target.value;
    setSelectedCampus(value);
    if (onCampusChange) onCampusChange(value);
  };

  return (
    <>
      <div className="mb-3">
        <select
          className={`form-select ${validationErrors.institution ? 'is-invalid' : ''}`}
          id="institutionSelect"
          value={selectedInstitution}
          onChange={handleInstitutionChange}
          disabled={disabled || institutionDisabled || loadingInstitutions}
        >
          <option value="">Select institution</option>
          {institutions.map((inst) => (
            <option key={inst.id} value={inst.id}>
              {inst.name}
            </option>
          ))}
        </select>
        {validationErrors.institution && (
          <div className="invalid-feedback">{validationErrors.institution}</div>
        )}
      </div>
      <div className="mb-3">
        <select
          className={`form-select ${validationErrors.campus ? 'is-invalid' : ''}`}
          id="campusSelect"
          value={selectedCampus}
          onChange={handleCampusChange}
          disabled={!selectedInstitution || disabled || campusDisabled || loadingCampuses}
        >
          <option value="">Select campus</option>
          {campuses.map((campus) => (
            <option key={campus.id} value={campus.id}>
              {campus.name}
            </option>
          ))}
        </select>
        {validationErrors.campus && (
          <div className="invalid-feedback">{validationErrors.campus}</div>
        )}
      </div>

      <style jsx="true">{`
        /* Custom styling for select fields */
        .form-select {
          height: 45px !important;
          min-height: 45px !important;
          padding: 0.5rem 0.75rem !important;
          background-color: white;
          border: 1px solid #ced4da;
          border-radius: 0.375rem;
        }
        
        .form-select:focus {
          border-color: #86b7fe;
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        /* MDB5 validation styles - matching StudentFormWizard */
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .form-control.is-invalid:focus,
        .form-select.is-invalid:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #fff;
          text-align: left;
          position: relative;
          z-index: 1;
          padding: 0.5rem 0.75rem;
          background: #dc3545;
          border-radius: 0.375rem;
          border: 1px solid #dc3545;
        }
        
        /* For form containers, ensure validation message doesn't overlap */
        .form-outline .invalid-feedback,
        .form-floating .invalid-feedback {
          margin-top: 0.5rem;
          position: relative;
          clear: both;
        }
        
        /* Add arrow pointer to validation message */
        .invalid-feedback::before {
          content: '';
          position: absolute;
          top: -5px;
          left: 1rem;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 5px solid #dc3545;
        }
      `}</style>
    </>
  );
};

export default InstitutionCampusDropdown;